import { format2TimeStamp } from '@/utils/date';
import { OccType, MemberType, MemberLevel } from '@/config/constant';
import { isNil, parseFen2Yuan, parseYuan2Fen } from '@/utils/number';
import getRandomId from '@/utils/uuid';
import checkInService from '@/api/checkIn';
import {
  createDefaultRentTime,
  findItemByKey,
  flatPriceList,
  getDayDepartureTime,
  getRentTimeInfo,
} from '@/redux/actions/reservation/helper';
import { MainState, ReducerState, RenewalRentRoomInfo } from '@/redux/reducers/reservation/renewal';
import { RoomGuestItem } from '@/redux/reducers/reservation/base.d';
import { createTargetGetter, TargetType } from '@/components/Reservation/PopupTarget';

// 获取续住单详情
export const getStateFromOrder: (
  resvNo: string,
  recNoList: string[],
) => Promise<MainState> = async (resvNo, recNoList) => {
  const res = await checkInService.getRenewalInfo({ recNoList, resvNo });
  const {
    customerInfoList,
    expectedDepartureTime,
    extensionRoomMsgVOList,
    masterWyuid,
    extensionId,
  } = res.data;
  const { dayTypeTimeInfo, hourTypeTimeInfo } = createDefaultRentTime(
    expectedDepartureTime || undefined,
  );

  const allGuestList: RoomGuestItem[] = []; // 房间宾客
  const rentRoomInfos: RenewalRentRoomInfo[] = []; // 入住房间
  const roomGuestIdMap: { [roomId: string]: string[] } = {}; // 房间id 宾客id
  let masterMemberLevel: MemberLevel; // 房主会员状态
  (customerInfoList || []).forEach((item) => {
    const {
      roomId,
      roomNo,
      certType,
      certNo,
      mobile,
      remainingBalance,
      memberType,
      name,
      isMember,
      // memberFlag,
      memberLevel,
    } = item;
    const guestId = getRandomId();
    const info: RoomGuestItem = {
      indexId: guestId,
      roomNo,
      name,
      memberType,
      certType,
      certNo,
      mobile,
      isMember,
      // memberFlag,
      memberLevel,
      remainingBalance,
    };
    const guestNameArr = roomGuestIdMap[roomId] || [];
    guestNameArr.push(guestId); // 房间宾客id
    roomGuestIdMap[roomId] = guestNameArr;
    if (memberType === MemberType.HouseOwner) {
      // 房主
      masterMemberLevel = memberLevel;
      allGuestList.unshift(info); // 房主在第一位
    } else {
      allGuestList.push(info);
    }
  });
  (extensionRoomMsgVOList || []).forEach((item) => {
    const { roomId, deposit, names } = item;
    rentRoomInfos.push({
      ...item,
      indexId: getRandomId(),
      guestNames: names,
      guestList: roomGuestIdMap[roomId] || [],
      deposit: parseFen2Yuan(deposit),
      roomPriceList: [],
    });
  });

  if (!isNil(masterMemberLevel) && masterMemberLevel !== MemberLevel.Common) {
    // 会员日租房离店时间14:00:00
    dayTypeTimeInfo.expectedDepartureTime = getDayDepartureTime(dayTypeTimeInfo, '14:00:00');
  }

  return {
    commonInfo: {
      occType: OccType.DayUse,
      memberLevel: masterMemberLevel,
      mainOrderId: resvNo,
      ownerWYId: masterWyuid,
      extensionId,
    },
    allGuestList,
    dayTypeTimeInfo,
    hourTypeTimeInfo,
    rentRoomInfos,
    channelInfo: {
      otaPaymentAmount: '',
      invoiceAmount: '',
    },
  };
};

// 提交数据
export const formatToSubmitData: (data: ReducerState) => { [key: string]: unknown } = (data) => {
  const {
    commonInfo,
    channelInfo,
    dayTypeTimeInfo,
    hourTypeTimeInfo,
    rentRoomInfos,
    allChannelList,
    strategyList,
    roomPriceList,
  } = data;
  const { mainOrderId, occType, extensionId } = commonInfo;
  const { channelId, strategyId, channelOrderNo, otaPaymentAmount, invoiceAmount } = channelInfo;
  const rentTimeInfo = getRentTimeInfo({ dayTypeTimeInfo, hourTypeTimeInfo }, occType);
  const sourceChannelName = findItemByKey(allChannelList, 'id', channelId)?.name;
  const priceStrategyName = findItemByKey(strategyList, 'id', strategyId)?.showName;
  const { cachePriceMap } = flatPriceList(roomPriceList);
  const dayInfo = [];
  (rentRoomInfos || []).forEach((room) => {
    const { roomPriceList: itemPriceList, recNo, roomTypeId } = room;
    const strategyPriceMap = cachePriceMap[roomTypeId] || {};
    (itemPriceList || []).forEach((rentDay) => {
      const {
        rentDate,
        inputPrice,
        couponAmount,
        couponName,
        couponNo,
        couponType,
        couponWyuid,
        couponSubType,
      } = rentDay;
      dayInfo.push({
        recNo,
        price: parseYuan2Fen(inputPrice),
        rentDay: format2TimeStamp(rentDate),
        strategyPrice: parseYuan2Fen(strategyPriceMap[rentDate]),
        couponAmount,
        couponName,
        couponNo,
        couponType,
        couponWyuid,
        couponSubType,
      });
    });
  });
  return {
    channelInfo: {
      channelOrderNo,
      otaPaymentAmount: parseYuan2Fen(otaPaymentAmount),
      invoiceAmount: parseYuan2Fen(invoiceAmount),
      priceStrategyId: strategyId,
      priceStrategyName,
      sourceChannelId: channelId,
      sourceChannelName,
    },
    dayInfo,
    rentTypeInfo: {
      expectedArrivalTime: format2TimeStamp(rentTimeInfo.expectedArrivalTime),
      expectedDepartureTime: format2TimeStamp(rentTimeInfo.expectedDepartureTime),
      occDuration: rentTimeInfo.rentTime,
      occType,
    },
    resvNo: mainOrderId,
    extensionId,
  };
};

// 定位节点
export const getPopupContainer = createTargetGetter(TargetType.Renewal);
